/**
 * Sessions — user-facing device manager (V2.0.41, Feb 22, 2026).
 *
 * Shows every live browser/device this account is signed into, with:
 *   - Device / browser label (parsed from the User-Agent)
 *   - "This device" pill on the current session
 *   - Last active timestamp (relative)
 *   - "Revoke" button per row (except current session, which uses
 *     the standard Logout button in the top nav)
 *   - "Sign out other devices" — one-click revoke everything except
 *     the current session.
 *
 * Reachable at:
 *   /settings/sessions            — recruiter/admin/HM/superadmin
 *   /candidate/settings/sessions  — candidate portal
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { DeviceMobile, DesktopTower, ShieldCheck, X, ArrowsClockwise } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";

function formatRelative(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const diffMs = Date.now() - d.getTime();
        const mins = Math.floor(diffMs / 60000);
        if (mins < 1) return "just now";
        if (mins < 60) return `${mins} min ago`;
        const hours = Math.floor(mins / 60);
        if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
        const days = Math.floor(hours / 24);
        if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
        return d.toLocaleDateString();
    } catch (e) { return "—"; }
}

export default function SessionsSettings() {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/auth/sessions");
            setSessions(r.data.items || []);
        } catch (e) {
            toast.error(formatApiError(e) || "Couldn't load sessions.");
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const revoke = useCallback(async (sid) => {
        setBusy(true);
        try {
            await api.post(`/auth/sessions/${sid}/revoke`);
            toast.success("Session revoked.");
            await load();
        } catch (e) {
            toast.error(formatApiError(e) || "Couldn't revoke session.");
        } finally { setBusy(false); }
    }, [load]);

    const logoutOthers = useCallback(async () => {
        const others = sessions.filter((s) => !s.is_current).length;
        if (others === 0) {
            toast.info("You're only signed in on this device.");
            return;
        }
        if (!window.confirm(`Sign out ${others} other device${others === 1 ? "" : "s"}?`)) return;
        setBusy(true);
        try {
            const r = await api.post("/auth/sessions/logout-others");
            toast.success(`Signed out ${r.data.revoked} device${r.data.revoked === 1 ? "" : "s"}.`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e) || "Couldn't sign out other devices.");
        } finally { setBusy(false); }
    }, [sessions, load]);

    return (
        <div className="max-w-3xl mx-auto p-4 sm:p-6" data-testid="sessions-page">
            <div className="flex items-start justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-black text-ink tracking-tight">
                        Signed-in devices
                    </h1>
                    <p className="text-sm text-ink/60 mt-1 max-w-xl">
                        Every browser and device currently signed into your account.
                        Revoke access to any you don't recognize — this will kick that
                        device out on its next server tick.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={load}
                    disabled={loading}
                    className="text-[10px] font-black uppercase tracking-wider text-ink/60 hover:text-ink disabled:opacity-40 inline-flex items-center gap-1"
                    title="Refresh"
                >
                    <ArrowsClockwise size={13} weight="bold" />
                    Refresh
                </button>
            </div>

            {loading && (
                <div className="text-sm text-ink/50" data-testid="sessions-loading">Loading…</div>
            )}
            {!loading && sessions.length === 0 && (
                <div className="text-sm text-ink/50">No active sessions.</div>
            )}

            <div className="space-y-2">
                {sessions.map((s) => {
                    const isMobile = /iPhone|Android|iPad/i.test(s.ua || "");
                    return (
                        <div
                            key={s.id}
                            data-testid={`session-row-${s.id}`}
                            className={`p-4 rounded-lg border flex items-start gap-3 ${
                                s.is_current ? "border-cobalt bg-cobalt/5" : "border-ink/10 bg-white"
                            }`}
                        >
                            <div className={`shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${
                                s.is_current ? "bg-cobalt/15 text-cobalt" : "bg-ink/5 text-ink/70"
                            }`}>
                                {isMobile ? <DeviceMobile size={20} weight="fill" /> : <DesktopTower size={20} weight="fill" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-black text-ink">{s.device_label}</span>
                                    {s.location_label && (
                                        <span
                                            data-testid={`session-location-${s.id}`}
                                            className="text-[10px] font-bold text-ink/60"
                                            title={`Approximate location from IP: ${s.location_label}`}
                                        >
                                            · {s.location_label}
                                        </span>
                                    )}
                                    {s.is_current && (
                                        <span className="text-[9px] font-black uppercase tracking-wider bg-cobalt text-white px-2 py-0.5 rounded-full">
                                            This device
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-ink/50 mt-0.5">
                                    Signed in {formatRelative(s.created_at)} · Last active {formatRelative(s.last_seen_at)}
                                </div>
                            </div>
                            {!s.is_current && (
                                <button
                                    type="button"
                                    onClick={() => revoke(s.id)}
                                    disabled={busy}
                                    data-testid={`session-revoke-${s.id}`}
                                    className="shrink-0 text-[10px] font-black uppercase tracking-wider text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-full inline-flex items-center gap-1 disabled:opacity-40"
                                >
                                    <X size={11} weight="bold" /> Revoke
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {sessions.length > 1 && (
                <div className="mt-6 p-4 rounded-lg border border-ink/10 bg-cream/40 flex items-start gap-3">
                    <ShieldCheck size={20} weight="bold" className="text-cobalt shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <div className="text-sm font-bold text-ink">Sign out everywhere else</div>
                        <div className="text-xs text-ink/60 mt-0.5">
                            Useful if you left your account signed in on a shared or public device.
                            This device stays signed in.
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={logoutOthers}
                        disabled={busy}
                        data-testid="sessions-logout-others-btn"
                        className="shrink-0 h-9 px-4 rounded-full bg-ink text-white text-[10px] font-black uppercase tracking-wider hover:opacity-90 disabled:opacity-40"
                    >
                        Sign out other devices
                    </button>
                </div>
            )}
        </div>
    );
}
